import React, { useState, useEffect, useMemo, useRef } from 'react'
import { LlamaCursorDownward } from '@easyllama/ui-components/icons'
import BillingComparisonTable from '~/molecules/Billing/BillingComparisonTable'
import ContactSalesDialog from '~/molecules/Billing/ContactSalesDialog'
import PlanCard from '~/molecules/Billing/PlanCard'
import PlanCardSkeleton from '~/molecules/Billing/PlanCardSkeleton'
import { PLAN_METADATA, getSeatRules } from '~/service/Billing/billingConstants'
import { formatPrice } from '~/service/Billing/utils'
import { fetchEstimate } from '~/service/Billing/fetchApiClient'
import { useApp } from '~/context/appContext'

function mergePlan(apiPlan) {
  const meta = PLAN_METADATA[apiPlan.unit] ?? {}
  return {
    id: apiPlan.unit,
    name: apiPlan.name,
    isCurrent: apiPlan.current,
    switchable: apiPlan.switchable,
    price: meta.priceLabel ?? formatPrice(apiPlan.cost_per_seat),
    periodUnit: meta.periodUnit ?? '',
    periodCycle: meta.periodCycle ?? '',
    description: meta.description ?? '',
    counterLabel: meta.counterLabel ?? 'seats',
    defaultCount: meta.defaultCount ?? 10,
    featureHeader: meta.featureHeader ?? null,
    legacyMessage: meta.legacyMessage ?? null,
    featureList: meta.featureList ?? [],
  }
}

export default function BillingPlansCards({ plans = [], onSelect, comparisonOpen, onComparisonOpen, isLoading = false, selectingPlanId = null, initialCount = null, initialUnit = null, showContactSales = true, hideCounterSubtext = false, restrictUnit = null }) {
  const { standardSeatQuantity, subscription, company, jwt, availablePlansCount } = useApp()
  const cardPlans = useMemo(() => plans.filter((p) => !p.comparisonOnly), [plans])

  const currentPlanFallback = useMemo(() => {
    const meta = PLAN_METADATA[subscription?.planLicense?.unit] ?? {}
    return { periodUnit: meta.periodUnit ?? '', periodCycle: meta.periodCycle ?? '' }
  }, [subscription])

  const mergedPlans = useMemo(() => cardPlans.map((apiPlan) => {
    const merged = mergePlan(apiPlan)
    if (apiPlan.current && !merged.periodUnit) {
      merged.periodUnit = currentPlanFallback.periodUnit
      merged.periodCycle = currentPlanFallback.periodCycle
    }
    return merged
  }), [cardPlans, currentPlanFallback])

  const { minCount } = getSeatRules(company)
  // Intuit companies start from their own 1-seat floor instead of the standard seat quantity
  const fallbackCount = company?.isIntuit ? minCount : standardSeatQuantity

  const defaultCount = (() => {
    const hasNoSubscriptionOrCanceled = !subscription || subscription.status === 'canceled'
    if (hasNoSubscriptionOrCanceled) {
      return Math.max(minCount, initialCount ?? fallbackCount ?? minCount)
    }
    const hasActiveSubscriptionWithHighLicenses = subscription && (subscription.licensesCount || 0) >= minCount
    if (hasActiveSubscriptionWithHighLicenses) {
      return initialCount ?? 0
    }
    return Math.max(minCount, initialCount ?? fallbackCount ?? minCount)
  })()

  const [counts, setCounts] = useState({})
  const [priceOverrides, setPriceOverrides] = useState({})
  const [counterLoading, setCounterLoading] = useState({})
  const [downgradeSalesOpen, setDowngradeSalesOpen] = useState(false)
  const debounceRefs = useRef({})
  const controllerRefs = useRef({})
  const countsLicensesCountRef = useRef(subscription?.licensesCount)

  useEffect(() => {
    const curr = subscription?.licensesCount
    if (countsLicensesCountRef.current === curr) return
    countsLicensesCountRef.current = curr
    setCounts({})
  }, [subscription?.licensesCount])

  useEffect(() => {
    if (mergedPlans.length === 0) return
    setPriceOverrides({})
  }, [mergedPlans])

  useEffect(() => {
    if (mergedPlans.length === 0) return
    setCounts((prev) => {
      const next = { ...prev }
      let changed = false
      mergedPlans.forEach((p) => {
        if (!(p.id in next)) {
          next[p.id] = defaultCount
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [mergedPlans, defaultCount])

  const selectedId = useMemo(() => {
    if (restrictUnit && mergedPlans.some((p) => p.id === restrictUnit)) {
      return restrictUnit
    }
    if (initialUnit && mergedPlans.length > 0) {
      const match = mergedPlans.find((p) => p.id === initialUnit)
      if (match?.switchable) return initialUnit
    }
    return 'ultimate'
  }, [initialUnit, restrictUnit, mergedPlans])

  useEffect(() => {
    return () => {
      Object.values(debounceRefs.current).forEach(clearTimeout)
      Object.values(controllerRefs.current).forEach((c) => c?.abort())
    }
  }, [])

  const handleCountChange = (planId, val) => {
    setCounts((prev) => ({ ...prev, [planId]: val }))
    clearTimeout(debounceRefs.current[planId])
    controllerRefs.current[planId]?.abort()
    controllerRefs.current[planId] = null
    debounceRefs.current[planId] = setTimeout(() => {
      setCounterLoading((prev) => ({ ...prev, [planId]: true }))
      const controller = new AbortController()
      controllerRefs.current[planId] = controller
      const isNewOrCanceledSubscription = !subscription || subscription.status === 'canceled'
      const quantity = isNewOrCanceledSubscription ? val : (subscription.licensesCount || 0) + val
      fetchEstimate(company.id, jwt, planId, quantity, controller.signal)
        .then((data) => {
          if (controllerRefs.current[planId] !== controller) return
          if (data.license_cost != null) {
            setPriceOverrides((prev) => ({ ...prev, [planId]: data.license_cost }))
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            setPriceOverrides((prev) => { const next = { ...prev }; delete next[planId]; return next })
          }
        })
        .finally(() => {
          if (controllerRefs.current[planId] !== controller) return
          setCounterLoading((prev) => { const next = { ...prev }; delete next[planId]; return next })
        })
    }, 500)
  }

  const handleSelect = (plan) => {
    onSelect(plan, counts[plan.id] ?? defaultCount)
  }

  if (!isLoading && mergedPlans.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-20 px-6 text-center '>
        <p className='text-lg font-semibold text-llama-grey-7 mb-2'>No plans available</p>
        <p className='text-sm text-llama-grey-5'>Please try again later or contact sales for assistance.</p>
      </div>
    )
  }

  return (
    <>
    {showContactSales && (
      <ContactSalesDialog
        open={downgradeSalesOpen}
        handleOpenChange={setDowngradeSalesOpen}
        title='Downgrade plan'
        description='To downgrade, please contact our sales team.'
      />
    )}
    <div className='lining-nums'>
      <div
        className='grid gap-4 pt-10 pb-10 px-8 items-stretch max-lg:!grid-cols-1'
        style={{ gridTemplateColumns: `repeat(${isLoading ? availablePlansCount : mergedPlans.length}, 1fr)` }}
      >
        {isLoading
          ? Array.from({ length: availablePlansCount }).map((_, i) => <PlanCardSkeleton key={i} />)
          : mergedPlans.map((plan, index) => {
            const isCurrentPlan = plan.isCurrent
            const hasNoSubscriptionOrCanceled = !subscription || subscription.status === 'canceled'
            const licensesCountHighEnough = !hasNoSubscriptionOrCanceled && subscription?.licensesCount >= minCount
            const isSelectCurrentPlanDisabled = isCurrentPlan && licensesCountHighEnough && (counts[plan.id] ?? defaultCount) === 0
            const isRestrictedOut = restrictUnit != null && plan.id !== restrictUnit
            return (
              <PlanCard
                key={plan.id}
                plan={priceOverrides[plan.id] != null ? { ...plan, price: priceOverrides[plan.id] } : plan}
                count={counts[plan.id] ?? defaultCount}
                isSelected={selectedId === plan.id}
                isHighlightCard={index === 0}
                onCountChange={(val) => handleCountChange(plan.id, val)}
                isCounterDisabled={!!counterLoading[plan.id] || isRestrictedOut}
                onSelect={handleSelect}
                onContactSales={showContactSales ? () => setDowngradeSalesOpen(true) : undefined}
                isSelectLoading={selectingPlanId === plan.id}
                isSelectDisabled={(selectingPlanId !== null && selectingPlanId !== plan.id) || isRestrictedOut}
                isSelectCurrentPlanDisabled={isSelectCurrentPlanDisabled}
                subscription={subscription}
                hideCounterSubtext={hideCounterSubtext}
              />
            )
          })}
      </div>

      <div className='text-center'>
        {!comparisonOpen && (
          <div
            role='button'
            tabIndex={0}
            onClick={onComparisonOpen}
            onKeyDown={(e) => e.key === 'Enter' && onComparisonOpen()}
            className='inline-flex items-center gap-1.5 cursor-pointer select-none pt-2 pb-18'
          >
            
            <LlamaCursorDownward size={24} className='transition-transform' />
            <span className='font-bold text-base text-llama-grey-10'>
              See feature comparison
            </span>
          </div>
        )}

        {comparisonOpen && (
          <BillingComparisonTable plans={plans} priceOverrides={priceOverrides} />
        )}
      </div>
    </div>
    </>
  )
}
